import { Fragment, ReactNode } from "react";
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress as MuiCircularProgress
} from "@mui/material";
import { ButtonSize, ButtonTheme } from "../button.constants";
import classnames from "classnames";
import styles from "./primary-button.styles.module.scss";

type PrimaryButtonProps = {
  children: ReactNode;
  theme?: `${ButtonTheme}`;
  size?: `${ButtonSize}`;
  ariaLabel?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  href?: string;
  startIcon?: MuiButtonProps["startIcon"];
  endIcon?: MuiButtonProps["endIcon"];
  onClick?: MuiButtonProps["onClick"];
  rootClassName?: string;
  additionalProps?: Partial<MuiButtonProps>;
};

export const PrimaryButtonComponent = ({
  children,
  theme = ButtonTheme.LIGHT,
  size = ButtonSize.DEFAULT,
  ariaLabel,
  isDisabled = false,
  isLoading = false,
  href,
  startIcon,
  endIcon,
  onClick,
  rootClassName = "",
  additionalProps = {}
}: PrimaryButtonProps) => {
  const handleClick: MuiButtonProps["onClick"] = (event) => {
    if (isLoading) {
      return;
    }
    onClick?.(event);
  };

  return (
    <MuiButton
      {...additionalProps}
      aria-label={ariaLabel}
      classes={{
        root: classnames(
          styles.button,
          styles.buttonRoot,
          {
            [ButtonTheme.LIGHT]: styles.buttonThemeLight,
            [ButtonTheme.LIGHT_DESTRUCTIVE]: styles.buttonThemeLightDest,
            [ButtonTheme.DARK]: styles.buttonThemeDark,
            [ButtonTheme.DARK_DESTRUCTIVE]: styles.buttonThemeDarkDest
          }[theme],
          size === ButtonSize.SMALL && styles.sizeSmall,
          rootClassName
        ),
        startIcon: classnames(
          startIcon && styles.buttonStartIcon,
          isLoading && styles.invisible
        ),
        endIcon: classnames(
          endIcon && styles.buttonEndIcon,
          isLoading && styles.invisible
        ),
        disabled: styles.buttonDisabled,
      }}
      TouchRippleProps={{
        classes: {
          root: styles.rippleRoot,
          ripple: styles.ripple,
        }
      }}
      disabled={isDisabled}
      disableElevation={true}
      disableFocusRipple={true}
      href={href}
      onClick={handleClick}
      startIcon={startIcon}
      endIcon={endIcon}
    >
      <Fragment>
        <span
          className={classnames(
            styles.text,
            isLoading && styles.invisible,
            size === ButtonSize.SMALL && styles.sizeSmall,
          )}
        >
          {children}
        </span>
        {isLoading &&
        <span className={styles.loaderContainer}>
          <MuiCircularProgress
            classes={{
              root: classnames(
                styles.loader,
                {
                  [ButtonTheme.LIGHT]: styles.loaderThemeDark,
                  [ButtonTheme.LIGHT_DESTRUCTIVE]: styles.loaderThemeLightDest,
                  [ButtonTheme.DARK]: styles.loaderThemeLight,
                  [ButtonTheme.DARK_DESTRUCTIVE]: styles.loaderThemeDarkDest
                }[theme]
              )
            }}
            size={24}
            variant={"indeterminate"}
          />
        </span>
        }
      </Fragment>
    </MuiButton>
  );
};
